import { Edit, MapPin, Package, Trash2, Truck, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import convoyDetailsCache from '../../services/convoyDetailsCache';
import rideScheduleCache from '../../services/rideScheduleCache';
import { typography } from '../../styles/typography';
import { driverApi } from '../Driver/driverApi';
import { getVehicleById } from '../Onboarding/vehicleOnboardingApi';
import { getSiteDetails } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import IconTextButton from '../ui/IconTextButton';

const RideDetails = ({ ride, onBack }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openOverlay } = useOverlay();
  const [convoyDetails, setConvoyDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleNames, setVehicleNames] = useState({});
  const [driverNames, setDriverNames] = useState({});
  const [dockingStationNames, setDockingStationNames] = useState({});

  useEffect(() => {
    if (ride && ride.convoy_details) {
      fetchConvoyDetails();
    } else {
      setLoading(false);
    }
  }, [ride]);

  const fetchConvoyDetails = async () => {
    if (!ride.convoy_details || ride.convoy_details.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const convoyData = ride.convoy_details;
    const vehicleIds = new Set();
    const driverIds = new Set();
    const dockingStationIds = new Set();

    // Collect all IDs
    convoyData.forEach(convoy => {
      if (convoy.vehicle_id) vehicleIds.add(convoy.vehicle_id);
      if (convoy.user_id) driverIds.add(convoy.user_id);
      if (convoy.docking_station) dockingStationIds.add(convoy.docking_station);
    });

    // Check cache first for vehicles, drivers, and sites
    const vehicleMap = {};
    const driverMap = {};
    const dockingMap = {};
    
    const uncachedVehicleIds = [];
    const uncachedDriverIds = [];
    const uncachedStationIds = [];

    // Check vehicle cache
    Array.from(vehicleIds).forEach(vehicleId => {
      const cachedName = convoyDetailsCache.getVehicle(vehicleId);
      if (cachedName) {
        vehicleMap[vehicleId] = cachedName;
      } else {
        uncachedVehicleIds.push(vehicleId);
      }
    });

    // Check all drivers cache first
    let allDrivers = convoyDetailsCache.getAllDrivers();
    if (allDrivers) {
      // Use cached drivers to populate driver map
      Array.from(driverIds).forEach(driverId => {
        const driver = allDrivers.find(
          d => d.user_id === driverId || d.employee_id === driverId
        );
        const driverName = driver?.user_name || driver?.name;
        if (driverName) {
          driverMap[driverId] = driverName;
          convoyDetailsCache.setDriver(driverId, driverName);
        } else {
          uncachedDriverIds.push(driverId);
        }
      });
    } else {
      // Need to fetch all drivers
      uncachedDriverIds.push(...Array.from(driverIds));
    }

    // Check site cache
    Array.from(dockingStationIds).forEach(stationId => {
      const cachedName = convoyDetailsCache.getSite(stationId);
      if (cachedName) {
        dockingMap[stationId] = cachedName;
      } else {
        uncachedStationIds.push(stationId);
      }
    });

    // If everything is cached, we're done
    if (uncachedVehicleIds.length === 0 && uncachedDriverIds.length === 0 && uncachedStationIds.length === 0) {
      console.log('📦 Using cached convoy details');
      setVehicleNames(vehicleMap);
      setDriverNames(driverMap);
      setDockingStationNames(dockingMap);
      setLoading(false);
      return;
    }

    // Fetch uncached data in parallel
    try {
      const fetchPromises = [];
      
      // Fetch uncached vehicles
      if (uncachedVehicleIds.length > 0) {
        fetchPromises.push(
          Promise.all(
            uncachedVehicleIds.map(async (vehicleId) => {
              try {
                const vehicle = await getVehicleById(vehicleId);
                const name = vehicle?.model_name || vehicleId;
                convoyDetailsCache.setVehicle(vehicleId, name);
                return { id: vehicleId, name };
              } catch (error) {
                console.error(`Error fetching vehicle ${vehicleId}:`, error);
                return { id: vehicleId, name: vehicleId };
              }
            })
          )
        );
      } else {
        fetchPromises.push(Promise.resolve([]));
      }

      // Fetch all drivers if not cached
      if (!allDrivers) {
        fetchPromises.push(
          (async () => {
            try {
              const drivers = await driverApi.fetchAllDrivers();
              if (drivers) {
                convoyDetailsCache.setAllDrivers(drivers);
              }
              return drivers || [];
            } catch (error) {
              console.error('Error fetching all drivers:', error);
              return [];
            }
          })()
        );
      } else {
        fetchPromises.push(Promise.resolve(allDrivers));
      }

      // Fetch uncached docking stations
      if (uncachedStationIds.length > 0) {
        fetchPromises.push(
          Promise.all(
            uncachedStationIds.map(async (stationId) => {
              try {
                const site = await getSiteDetails(stationId);
                const name = site?.site_name || 'Unknown Station';
                convoyDetailsCache.setSite(stationId, name);
                return { id: stationId, name };
              } catch (error) {
                console.error(`Error fetching docking station ${stationId}:`, error);
                return { id: stationId, name: 'Unknown Station' };
              }
            })
          )
        );
      } else {
        fetchPromises.push(Promise.resolve([]));
      }

      const [vehicleData, fetchedDrivers, dockingData] = await Promise.all(fetchPromises);

      // Merge cached and fetched vehicle data
      vehicleData.forEach(v => { vehicleMap[v.id] = v.name; });

      // Merge cached and fetched driver data
      if (fetchedDrivers && fetchedDrivers.length > 0) {
        Array.from(driverIds).forEach(driverId => {
          if (!driverMap[driverId]) {
            const driver = fetchedDrivers.find(
              d => d.user_id === driverId || d.employee_id === driverId
            );
            const driverName = driver?.user_name || driver?.name || 'Unknown Driver';
            driverMap[driverId] = driverName;
            convoyDetailsCache.setDriver(driverId, driverName);
          }
        });
      }

      // Merge cached and fetched docking station data
      dockingData.forEach(s => { dockingMap[s.id] = s.name; });

      setVehicleNames(vehicleMap);
      setDriverNames(driverMap);
      setDockingStationNames(dockingMap);
    } catch (error) {
      console.error('Error fetching convoy details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const handleEdit = () => {
    // Navigate to create ride schedule with edit mode
    setSearchParams({ view: 'create-ride', rideId: ride.ride_id });
  };

  const deleteRide = async (rideId) => {
    try {
      console.log('🗑️ Deleting ride:', rideId);
      
      // Call the API to delete the ride schedule
      await driverApi.deleteDriverSchedule(rideId);
      
      console.log('✅ Ride deleted successfully');
      
      // Clear the ride schedule cache so the parent component will fetch fresh data
      rideScheduleCache.clear();
      
      // Set flag to trigger refresh in parent component
      sessionStorage.setItem('shouldRefreshRideSchedules', 'true');
      
      // Show success message
      alert('Ride deleted successfully!');
      
      // Navigate back to the schedule list
      setSearchParams({});
      
      // Also call onBack if provided (as a fallback)
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error('❌ Error deleting ride:', error);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Failed to delete ride. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleDelete = () => {
    // Show delete warning overlay
    openOverlay('deleteRideWarning', {
      rideId: ride.ride_id,
      onConfirm: deleteRide
    });
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="mb-4 flex justify-between items-center">
        <h3 className={typography.h3} style={{ color: '#000000', marginBottom: 0 }}>
          Ride Convoy Details
        </h3>
        <div className="flex gap-3">
          <IconTextButton
            icon={<Edit className="w-6 h-6 text-white" />}
            text="Edit"
            onClick={handleEdit}
            size="md"
            variant="default"
          />
          <IconTextButton
            icon={<Trash2 className="w-6 h-6" style={{ color: '#ffffff' }} />}
            text="Delete"
            onClick={handleDelete}
            size="md"
            variant="danger"
          />
        </div>
      </div>

      {/* Ride Information */}
      <Card.NoShadowGradient className="mb-6 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm font-semibold text-gray-600 mb-1">From Site</div>
            <div className={typography.h5} style={{ color: '#000000' }}>
              {ride.fromSiteName || 'Unknown'}
            </div>
            {ride.fromSupervisorName && (
              <div className="text-sm text-gray-600 mt-1">
                Assigned Employee: {ride.fromSupervisorName}
              </div>
            )}
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-600 mb-1">To Site</div>
            <div className={typography.h5} style={{ color: '#000000' }}>
              {ride.toSiteName || 'Unknown'}
            </div>
            {ride.toSupervisorName && (
              <div className="text-sm text-gray-600 mt-1">
                Assigned Employee: {ride.toSupervisorName}
              </div>
            )}
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-600 mb-1">Start Time</div>
            <div className={typography.body1} style={{ color: '#000000' }}>
              {formatTimestamp(ride.ride_start_timestamp)}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-600 mb-1">End Time</div>
            <div className={typography.body1} style={{ color: '#000000' }}>
              {formatTimestamp(ride.ride_end_timestamp)}
            </div>
          </div>
          {ride.average_speed && (
            <div>
              <div className="text-sm font-semibold text-gray-600 mb-1">Average Speed</div>
              <div className={typography.body1} style={{ color: '#000000' }}>
                {ride.average_speed} km/h
              </div>
            </div>
          )}
        </div>
      </Card.NoShadowGradient>

      {/* Convoy Details Table */}
      <div className="flex-1 overflow-auto">
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading convoy details...</div>
          </div>
        ) : !ride.convoy_details || ride.convoy_details.length === 0 ? (
          <Card.Gradient className="p-6">
            <div className="text-center text-gray-500">
              No convoy details available
            </div>
          </Card.Gradient>
        ) : (
          <div className="overflow-x-auto">
            <div className="rounded-xl overflow-hidden">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 rounded-tl-xl border-r border-gray-300">
                      S.no
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 border-r border-gray-300">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        Vehicles
                      </div>
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 border-r border-gray-300">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Driver
                      </div>
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 border-r border-gray-300">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Parking Destination
                      </div>
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 rounded-tr-xl">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Material
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ride.convoy_details.map((convoy, index) => {
                    const vehicleId = convoy.vehicle_id;
                    const driverId = convoy.user_id;
                    const dockingStationId = convoy.docking_station || '';
                    const material = convoy.material || 'No Material';
                    const isLastRow = index === ride.convoy_details.length - 1;
                    const rowBg = index % 2 === 0 ? 'bg-white/50 backdrop-blur-sm' : 'bg-white/70 backdrop-blur-sm';

                    return (
                      <tr key={index}>
                        <td className={`border border-gray-300 px-4 py-3 border-r border-gray-300 ${rowBg} ${isLastRow ? 'rounded-bl-xl' : ''}`}>
                          {index + 1}
                        </td>
                        <td className={`border border-gray-300 px-4 py-3 border-r border-gray-300 ${rowBg}`}>
                          {loading ? (
                            <span className="text-gray-400">Loading...</span>
                          ) : (
                            vehicleNames[vehicleId] || vehicleId || 'N/A'
                          )}
                        </td>
                        <td className={`border border-gray-300 px-4 py-3 border-r border-gray-300 ${rowBg}`}>
                          {loading ? (
                            <span className="text-gray-400">Loading...</span>
                          ) : (
                            driverNames[driverId] || 'Unknown Driver'
                          )}
                        </td>
                        <td className={`border border-gray-300 px-4 py-3 border-r border-gray-300 ${rowBg}`}>
                          {loading ? (
                            <span className="text-gray-400">Loading...</span>
                          ) : dockingStationId ? (
                            dockingStationNames[dockingStationId] || dockingStationId || 'N/A'
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className={`border border-gray-300 px-4 py-3 ${rowBg} ${isLastRow ? 'rounded-br-xl' : ''}`}>
                          {material}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RideDetails;

